import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Stack,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CustomerBankAccountsGrid from "./CustomerBankAccountsGrid";
import {
  customerCardPanelHeaderSx,
  customerCardPanelLabelSx,
  customerCardPanelTitleTypographyProps,
  customerCardPanelValueSx,
} from "./customerCardPanelStyles";

const LineItem = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "220px minmax(0, 1fr)",
      columnGap: 1,
      alignItems: "start",
    }}
  >
    <Typography variant="body2" sx={customerCardPanelLabelSx}>
      {label}:
    </Typography>{" "}
    <Typography variant="body2" sx={customerCardPanelValueSx}>
      {value || "-"}
    </Typography>
  </Box>
);

const CustomerFinancialSummaryPanel = ({ customerId }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isBankAccountsDialogOpen, setIsBankAccountsDialogOpen] = useState(false);

  const fetchBankAccounts = useCallback(async () => {
    if (!customerId) {
      setBankAccounts([]);
      return;
    }

    try {
      const response = await axios.get(`/api/v1/bankaccountsByEntityId/${customerId}`);
      setBankAccounts(response.data || []);
    } catch (error) {
      console.error("Error loading bank accounts:", error);
    }
  }, [customerId]);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  useEffect(() => {
    const fetchCountries = async () => {
      const response = await axios.get("/api/v1/countries");
      setCountries(response.data || []);
    };

    fetchCountries().catch((error) => console.error("Error loading countries:", error));
  }, []);

  const handleBankAccountsChange = useCallback((accounts) => {
    setBankAccounts(accounts || []);
  }, []);

  const activeBankAccount = useMemo(
    () => bankAccounts.find((item) => item.isDefault) || bankAccounts[0],
    [bankAccounts]
  );

  const countryShortCode = useMemo(() => {
    if (!activeBankAccount) {
      return "-";
    }

    const countryById = countries.find((item) => item.countryId === activeBankAccount.countryCode);
    if (countryById?.countryCode) {
      return countryById.countryCode;
    }

    if (typeof activeBankAccount.countryCode === "string") {
      return activeBankAccount.countryCode;
    }

    return "-";
  }, [activeBankAccount, countries]);

  const handleOpenBankAccountsDialog = () => {
    setIsBankAccountsDialogOpen(true);
  };

  const handleCloseBankAccountsDialog = () => {
    setIsBankAccountsDialogOpen(false);
    fetchBankAccounts();
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Financial"
          titleTypographyProps={customerCardPanelTitleTypographyProps}
          sx={customerCardPanelHeaderSx}
          action={
            <Tooltip title="View bank accounts">
              <IconButton
                aria-label="view bank accounts"
                onClick={handleOpenBankAccountsDialog}
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <Divider />
        <CardContent sx={{ px: 2, pt: 1, pb: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Stack spacing={0.75}>
            <LineItem label="Active Bank Account" value={activeBankAccount?.iban || "No active bank account"} />
            <LineItem label="Country" value={countryShortCode} />
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={isBankAccountsDialogOpen} onClose={handleCloseBankAccountsDialog} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Bank Accounts
          <IconButton aria-label="close bank accounts dialog" onClick={handleCloseBankAccountsDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <CustomerBankAccountsGrid
            customerId={customerId}
            onBankAccountsChange={handleBankAccountsChange}
            gridHeight={400}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={handleCloseBankAccountsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerFinancialSummaryPanel;
